import { useState } from 'react';

export type DensityThresholds = [number, number, number];
export type DensityColors = [string, string, string, string];
export interface DensityLegendSettings { thresholds: DensityThresholds; colors: DensityColors }
export interface DensityLegendEntry { label: string; color: string }

/** NEMO.Market defaults (map dataset colours, hex allowed). */
export const DEFAULT_DENSITY_LEGEND: DensityLegendSettings = {
  thresholds: [1.5, 3, 4.5],
  colors: ['#bd5a21', '#ffeaa1', '#a2daa3', '#316336'],
};
export const NO_VALUE = { label: 'Sans valeur', color: '#ff0000' } as const;
export const LOOPS = { label: 'Boucles', color: '#000000' } as const;
export const DENSITY_UNIT = 'MWhu/m';
export const DENSITY_LEGEND_STORAGE_KEY = 'studio.urbanmap.nouveau-panel-selection.density-legend';

/** Colour choices of the editor; the reserved Sans valeur / Boucles colours are excluded. */
export const DENSITY_PALETTE: DensityLegendEntry[] = [
  { label: 'Brun-orangé', color: '#bd5a21' },
  { label: 'Jaune clair', color: '#ffeaa1' },
  { label: 'Vert clair', color: '#a2daa3' },
  { label: 'Vert foncé', color: '#316336' },
  { label: 'Gris', color: '#ccd4da' },
  { label: 'Orange', color: '#ef6b26' },
  { label: 'Jaune', color: '#ffd94a' },
  { label: 'Jaune-vert', color: '#c0d035' },
  { label: 'Vert', color: '#29a84a' },
  { label: 'Brun', color: '#8c4a1b' },
];

export const formatDensity = (value: number) => value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export function densityClass(value: number, thresholds: DensityThresholds = DEFAULT_DENSITY_LEGEND.thresholds): number {
  return value <= thresholds[0] ? 0 : value <= thresholds[1] ? 1 : value <= thresholds[2] ? 2 : 3;
}

export function densityClassLabels([a, b, c]: DensityThresholds): string[] {
  const f = formatDensity;
  return [`≤ ${f(a)} ${DENSITY_UNIT}`, `> ${f(a)} à ≤ ${f(b)} ${DENSITY_UNIT}`,
    `> ${f(b)} à ≤ ${f(c)} ${DENSITY_UNIT}`, `> ${f(c)} ${DENSITY_UNIT}`];
}

/** Four editable classes, then the two fixed states. */
export function densityLegendEntries(settings: DensityLegendSettings): DensityLegendEntry[] {
  return [...densityClassLabels(settings.thresholds).map((label, i) => ({ label, color: settings.colors[i] })),
    NO_VALUE, LOOPS];
}

/** Accepts French or English decimals ("1,5" / "1.5"); null when not a non-negative number. */
export function parseThreshold(raw: string): number | null {
  const text = raw.trim().replace(',', '.');
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : null;
}

export interface ThresholdCheck { thresholds?: DensityThresholds; error?: string; invalid: boolean[] }

export function validateThresholds(raw: string[]): ThresholdCheck {
  const values = raw.map(parseThreshold);
  if (values.some((v) => v === null)) {
    return { error: 'Saisissez un nombre positif pour chaque seuil.', invalid: values.map((v) => v === null) };
  }
  const numbers = values as number[];
  const invalid = numbers.map((v, i) => i > 0 && v <= numbers[i - 1]);
  if (invalid.some(Boolean)) return { error: 'Les seuils doivent être strictement croissants.', invalid };
  return { thresholds: numbers as DensityThresholds, invalid };
}

function isSettings(value: unknown): value is DensityLegendSettings {
  const v = value as Partial<DensityLegendSettings> | null;
  return !!v && Array.isArray(v.thresholds) && v.thresholds.length === 3
    && v.thresholds.every((t, i, all) => Number.isFinite(t) && t >= 0 && (i === 0 || t > all[i - 1]))
    && Array.isArray(v.colors) && v.colors.length === 4
    && v.colors.every((c) => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c));
}

export function loadDensityLegend(): DensityLegendSettings {
  try {
    const stored = JSON.parse(window.localStorage.getItem(DENSITY_LEGEND_STORAGE_KEY) ?? 'null');
    return isSettings(stored) ? stored : DEFAULT_DENSITY_LEGEND;
  } catch {
    return DEFAULT_DENSITY_LEGEND;
  }
}

/** Browser-only persistence (prototype): shared by both legends and every selection. */
export function useDensityLegend() {
  const [legend, setLegend] = useState<DensityLegendSettings>(loadDensityLegend);
  const saveLegend = (next: DensityLegendSettings) => {
    setLegend(next);
    try { window.localStorage.setItem(DENSITY_LEGEND_STORAGE_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };
  return { legend, saveLegend };
}
